/**
 * Board 3's popups on the quote page (`docs/job-stages.md` §5, §8) — C52a's
 * components, opened by the bar, ⋯ or the sticky bar's green button for the
 * quote they were offered on. Each fetches what it needs and calls `onDone`
 * only once the server said yes; the page then reads the quote and its
 * buttons again and says what changed in a white toast (findings §11), in
 * §14's History words. Cancel job has no toast (lead, Oct 30).
 */
import { ApproveQuote } from '../job-stages/approve-quote.js'
import { MarkQuoted } from '../job-stages/mark-quoted.js'
import { WithdrawQuote } from '../job-stages/withdraw-quote.js'
import { MarkDeclined } from '../job-stages/mark-declined.js'
import { CancelJob } from '../job-stages/cancel-job.js'
import { TOAST, quoteVersion } from '../job-stages/copy.js'
import type { QuoteBuilder } from '../builder/adapters/quote.js'
import type { QuoteStage } from './quote-stage.js'

export function QuoteStagePopups({ qb, stage }: { qb: QuoteBuilder; stage: QuoteStage }) {
  const { quote } = qb
  const { popup, closePopup, done } = stage
  if (!quote || !popup) return null
  /* The quote the action was offered for — this one, as Board 2 offers every action. */
  const target = popup.item.quote ?? {
    id: quote.id, reference: quote.reference, version: quote.quoteVersion,
  }
  const named = quoteVersion(target.reference, target.version)

  switch (popup.key) {
    case 'approve':
      return (
        <ApproveQuote
          open
          quoteId={target.id}
          onClose={closePopup}
          onDone={(answer) => done(answer.approvedByName ? TOAST.approved(named, answer.approvedByName) : null)}
        />
      )
    case 'mark_quoted':
      /* From a draft's ⋯: only that draft (§5). */
      return (
        <MarkQuoted
          open
          jobId={quote.jobId}
          quoteId={target.id}
          onClose={closePopup}
          onDone={() => done(TOAST.markedQuoted(named))}
        />
      )
    case 'withdraw_quote':
      return (
        <WithdrawQuote
          open
          jobId={quote.jobId}
          quoteId={target.id}
          onClose={closePopup}
          onDone={() => done(TOAST.withdrawn(named))}
        />
      )
    case 'mark_declined':
      return (
        <MarkDeclined
          open
          jobId={quote.jobId}
          quoteId={target.id}
          onClose={closePopup}
          onDone={() => done(TOAST.declined(named))}
        />
      )
    case 'cancel_job':
      return <CancelJob open jobId={quote.jobId} onClose={closePopup} onDone={() => done(null)} />
    default:
      return null
  }
}
